/* eslint-disable jira/choreographer/message-id-by-folder-structure */
/* eslint-disable jira/integration/test-id-by-folder-structure */
/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React, { memo, useMemo } from 'react';
import { jsx } from '@compiled/react';
import ShortcutScope from '@jira/platform__common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import {
	ContextualAnalyticsData,
	MODAL,
	FireScreenAnalytics,
	useAnalyticsEvents,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { ModalClosehandler } from './BoardCreateFormValues.types.tsx';
import { descriptionMapping } from './fireBoardCreateErrorAnalytics.tsx';
import { BoardCreateContext } from './useBoardCreateContext.tsx';
import { BoardCreateForm, BoardCreateFormFallback } from './BoardCreateForm.tsx';
import type { BoardCreateModalEntryPointProps } from './BoardCreateModal.entrypoint.types.tsx';

const boardCreationTitle = defineMessage({
	id: 'software-board-create.common.board-creation-title',
	defaultMessage: 'Create a board',
	description:
		'The title for both the board creation flyout menu that allows a user to pick between a creating a scrum or kanban board as well as the modal that opens after the user has made a selection',
});

const loadingLabel = defineMessage({
	id: 'software-board-create.modal.loading-label',
	defaultMessage: 'Loading',
	description: 'Aria label for loading component',
});

const MODAL_WIDTH = 1000;
const MODAL_HEIGHT = 551;

export const BoardCreateModalFallback = (): JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Modal
			height={MODAL_HEIGHT}
			width={MODAL_WIDTH}
			label={formatMessage(loadingLabel)}
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId="software-board-create.ui.modal.modal.board-create-modal-fallback"
			messageType="transactional"
		>
			<BoardCreateFormFallback />
		</Modal>
	);
};

const BoardCreateModal = ({
	boardType,
	projectId,
	onClose,
	triggerRef,
}: Omit<BoardCreateModalEntryPointProps, 'projectAvatar'>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleOnClose: ModalClosehandler = (_mouseEvent, _analyticEvent, didSubmit) => {
		const event = createAnalyticsEvent({
			action: 'closed',
			actionSubject: 'modalDialog',
		});

		fireUIAnalytics(event, 'createNewBoardModal', {
			didSubmit: !!didSubmit,
			boardType: boardType.toLowerCase(),
		});
		onClose?.();
	};

	return (
		<Modal
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="software-board-create.ui.modal"
			onClose={handleOnClose}
			width={MODAL_WIDTH}
			height={MODAL_HEIGHT}
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId="software-board-create.ui.modal.modal.board-create-modal"
			messageType="transactional"
			label={`${formatMessage(boardCreationTitle)}. ${formatMessage(descriptionMapping[boardType])}`}
			shouldReturnFocus={triggerRef}
		>
			<BoardCreateForm projectId={projectId} boardType={boardType} onClose={handleOnClose} />
			<FireScreenAnalytics attributes={{ boardType: boardType.toLowerCase() }} />
		</Modal>
	);
};

const BoardCreateModalWithContext = ({
	props: { projectAvatar, ...restProps },
}: {
	props: BoardCreateModalEntryPointProps;
}): JSX.Element => {
	const value = useMemo(
		() => ({ projectId: restProps.projectId, projectAvatar }),
		[restProps.projectId, projectAvatar],
	);

	return (
		<BoardCreateContext.Provider value={value}>
			<ShortcutScope>
				<ContextualAnalyticsData sourceName="createNewBoard" sourceType={MODAL}>
					<BoardCreateModal {...restProps} />
				</ContextualAnalyticsData>
			</ShortcutScope>
		</BoardCreateContext.Provider>
	);
};

const MemoizedBoardCreateModalWithContext: React.MemoExoticComponent<
	typeof BoardCreateModalWithContext
> = memo(BoardCreateModalWithContext);

export default MemoizedBoardCreateModalWithContext;
